import { Component, ChangeDetectionStrategy, input, output, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CorrectionWithUser } from '../../models/correction.model';

export type SortableCorrectionColumns = 'aluno' | 'created_at' | 'notaFinal';

@Component({
  selector: 'app-corrections-management',
  standalone: true,
  template: `
<div class="bg-white p-6 md:p-8 rounded-2xl shadow-card border border-slate-200/80">
  <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
    <div>
       <h2 class="text-2xl font-bold text-slate-900">Gerenciamento de Redações</h2>
       <p class="text-sm text-gray-500">Visualize todas as redações enviadas na plataforma.</p>
    </div>
  </div>

  <div class="mb-4">
    <label for="correction-search" class="sr-only">Buscar por aluno</label>
    <div class="relative">
      <div class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
         <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="w-5 h-5 text-gray-400"><path fill-rule="evenodd" d="M9 3.5a5.5 5.5 0 1 0 0 11 5.5 5.5 0 0 0 0-11ZM2 9a7 7 0 1 1 12.452 4.391l3.328 3.329a.75.75 0 1 1-1.06 1.06l-3.329-3.328A7 7 0 0 1 2 9Z" clip-rule="evenodd" /></svg>
      </div>
      <input type="text" id="correction-search" [value]="filterTerm()" (input)="onFilter($event)" class="block w-full rounded-lg border border-gray-300 dark:border-gray-600 pl-10 py-2 text-slate-800 dark:text-gray-200 dark:bg-gray-700 dark:border-gray-600 placeholder-gray-500 focus:border-custom-blue focus:ring-custom-blue sm:text-sm" placeholder="Buscar por nome ou email do aluno..."/>
    </div>
  </div>
  
  <div class="overflow-x-auto">
    <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
      <thead class="bg-custom-gray dark:bg-gray-700">
        <tr>
          <th scope="col" (click)="sortBy('aluno')" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer">Aluno</th>
          <th scope="col" (click)="sortBy('created_at')" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer">Data de Envio</th>
          <th scope="col" (click)="sortBy('notaFinal')" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer">Nota Final</th>
          <th scope="col" class="relative px-6 py-3"><span class="sr-only">Ações</span></th>
        </tr>
      </thead>
      <tbody class="bg-white divide-y divide-gray-200 dark:divide-gray-700">
        @for (correction of paginatedCorrections(); track correction.id) {
          <tr class="hover:bg-custom-gray dark:hover:bg-gray-700/50">
            <td class="px-6 py-4 whitespace-nowrap"><div class="text-sm font-medium">{{ correction.profiles?.name || 'Usuário Anônimo' }}</div><div class="text-sm text-gray-500">{{ correction.profiles?.email || '-' }}</div></td>
            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ correction.created_at | date: 'dd/MM/yyyy HH:mm' }}</td>
            <td class="px-6 py-4 whitespace-nowrap"><span class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full" [class]="getScoreColor(correction.data.notaFinal)">{{ correction.data.notaFinal.toFixed(1) }}</span></td>
            <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium"><button (click)="onView(correction)" class="text-custom-blue hover:text-blue-700 font-semibold">Ver Correção</button></td>
          </tr>
        } @empty {
          <tr><td colspan="4" class="px-6 py-4 text-center text-gray-500">Nenhuma redação encontrada.</td></tr>
        }
      </tbody>
    </table>
  </div>
  <div class="flex items-center justify-between mt-4 text-sm">
    <span class="text-gray-600 dark:text-gray-400">Página {{ currentPage() }} de {{ totalPages() }}</span>
    <div class="flex items-center space-x-2">
      <button (click)="changePage(currentPage() - 1)" [disabled]="currentPage() === 1" class="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed">Anterior</button>
      <button (click)="changePage(currentPage() + 1)" [disabled]="currentPage() === totalPages()" class="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed">Próximo</button>
    </div>
  </div>
</div>`,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, FormsModule],
})
export class CorrectionsManagementComponent {
  corrections = input.required<CorrectionWithUser[]>();
  viewCorrection = output<CorrectionWithUser>();

  readonly filterTerm = signal('');
  readonly sortColumn = signal<SortableCorrectionColumns>('created_at');
  readonly sortDirection = signal<'asc' | 'desc'>('desc');
  readonly currentPage = signal(1);
  readonly pageSize = signal(10);

  readonly processedCorrections = computed(() => {
    const term = this.filterTerm().toLowerCase();
    const column = this.sortColumn();
    const direction = this.sortDirection();

    const corrections = this.corrections().filter(c =>
      (c.profiles?.name || '').toLowerCase().includes(term) ||
      (c.profiles?.email || '').toLowerCase().includes(term)
    );

    corrections.sort((a, b) => {
      let aValue: string | number = '';
      let bValue: string | number = '';

      switch (column) {
        case 'aluno':
          aValue = a.profiles?.name || '';
          bValue = b.profiles?.name || '';
          break;
        case 'notaFinal':
          aValue = a.data.notaFinal;
          bValue = b.data.notaFinal;
          break;
        case 'created_at':
        default:
          aValue = new Date(a.created_at).getTime();
          bValue = new Date(b.created_at).getTime();
          break;
      }

      const comparison = String(aValue).localeCompare(String(bValue), undefined, { numeric: true });
      return direction === 'asc' ? comparison : -comparison;
    });

    return corrections;
  });

  readonly totalPages = computed(() => Math.ceil(this.processedCorrections().length / this.pageSize()));

  readonly paginatedCorrections = computed(() => {
    const startIndex = (this.currentPage() - 1) * this.pageSize();
    return this.processedCorrections().slice(startIndex, startIndex + this.pageSize());
  });

  onFilter(event: Event) {
    this.currentPage.set(1);
    const input = event.target as HTMLInputElement;
    this.filterTerm.set(input.value);
  }

  sortBy(column: SortableCorrectionColumns) {
    if (this.sortColumn() === column) {
      this.sortDirection.update(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      this.sortColumn.set(column);
      this.sortDirection.set('asc');
    }
    this.currentPage.set(1);
  }

  changePage(page: number) {
    if (page > 0 && page <= this.totalPages()) {
      this.currentPage.set(page);
    }
  }

  onView(correction: CorrectionWithUser) {
    this.viewCorrection.emit(correction);
  }

  getScoreColor(score: number): string {
    if (score >= 8) return 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300';
    if (score >= 6) return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-300';
    return 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300';
  }
}
